import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import { useEffect, useState } from 'react';
import DocumentOrchestrator from "../editors/composer/DocumentOrchestrator";
import PdfDocumentEditorPage from "../../dms/routes/PdfDocumentEditorPage";
import { useUIStore } from "../../../store/uiStore";
import apiClient from "../../../api/axios";

type EditorMode = 'resolving' | 'pdf' | 'edgedocs' | 'msword';

export default function EdgeDocsEditorPage() {
  // Handle both route parameter names
  // Route 1: /documents/:documentId/editor (used by DMSRightPanel - Build Document)
  // Route 2: /dms/editor/:docId (used by DMSManager - Open Editor)
  const params = useParams<{ documentId?: string; docId?: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const setHeaderVisible = useUIStore((state: any) => state.setHeaderVisible);

  // Extract docId from either route param name
  const docId = params.documentId || params.docId || '';

  // Extract versionId from query parameters
  // Support both ?versionId=... (standard) and ?version=... (legacy)
  const versionId = searchParams.get('versionId') || searchParams.get('version') || undefined;
  // Extract title from query parameters (passed from DMSRightPanel to avoid cache race condition)
  const titleFromUrl = searchParams.get('title') || undefined;

  // Detect MIME type to route PDFs to the PDF editor
  const [editorMode, setEditorMode] = useState<EditorMode>('resolving');

  useEffect(() => {
    if (!docId || docId.trim() === '') return;
    let canceled = false;

    (async () => {
      try {
        const response = await apiClient.get(`/dms/documents/${docId}`);
        if (canceled) return;

        const doc = response.data?.document || response.data;

        // Check editor_type first — Word documents redirect to word-editor
        if (doc?.editor_type === 'msword') {
          setEditorMode('msword');
          return;
        }

        const mime = (
          doc?.latest_version?.mime_type || doc?.mime_type || ''
        ).toLowerCase();

        setEditorMode(mime.includes('application/pdf') ? 'pdf' : 'edgedocs');
      } catch {
        // On failure, fall back to edgedocs (existing behavior)
        if (!canceled) setEditorMode('edgedocs');
      }
    })();

    return () => { canceled = true; };
  }, [docId]);

  // Guard against empty docId to prevent invalid API calls
  if (!docId || docId.trim() === '') {
    return (
      <div className="p-6 text-red-600 dark:text-red-400">
        <h2 className="text-xl font-bold mb-2">Error: Invalid Document ID</h2>
        <p>The document ID is missing. Please navigate to a valid document.</p>
      </div>
    );
  }

  // Hide global header while editor is active
  useEffect(() => {
    setHeaderVisible(false);
    return () => { setHeaderVisible(true); };
  }, [setHeaderVisible]);

  if (editorMode === 'resolving') {
    return (
      <div className="flex items-center justify-center h-screen bg-[var(--brand-surface-app)]">
        <div className="text-sm text-[var(--brand-text-secondary)]">Loading document…</div>
      </div>
    );
  }

  // Redirect Word documents to the Word document page
  if (editorMode === 'msword') {
    const urlParams = new URLSearchParams();
    if (versionId) urlParams.set('version', versionId);
    if (titleFromUrl) urlParams.set('title', titleFromUrl);
    navigate(`/documents/${docId}/word-editor${urlParams.toString() ? `?${urlParams.toString()}` : ''}`, { replace: true });
    return null;
  }

  if (editorMode === 'pdf') {
    return <PdfDocumentEditorPage docId={docId} versionId={versionId} titleFromUrl={titleFromUrl} />;
  }

  // Non-PDF: use DocumentOrchestrator with EdgeDocs word editor
  return <DocumentOrchestrator docId={docId} versionId={versionId} initialTitle={titleFromUrl} preferredEditor="edgedocs" />;
}
